import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Home as HomeIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { apiClient } from '@/utils/api-client';
import { useAuth } from '@/contexts/AuthContext';

interface Conversation {
  bookingId: string;
  listingTitle: string;
  checkInDate: string;
  checkOutDate: string;
  status: string;
  userRole: 'host' | 'guest';
  section: 'host' | 'guest';
  otherParticipant: {
    email: string;
    name: string;
    type: 'host' | 'guest';
  };
  lastMessage?: {
    content: string;
    createdAt: string;
    senderEmail: string;
    senderType: 'host' | 'guest';
  };
  unreadCount: number;
  lastMessageAt: string;
  createdAt: string;
}

interface ConversationsListProps {
  onConversationSelect: (conversation: Conversation) => void;
  selectedBookingId?: string;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  onConversationSelect,
  selectedBookingId
}) => {
  const { userProfile: user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading conversations from existing APIs...');
      console.log('User info:', {
        email: user?.email,
        primaryRole: user?.primaryRole,
        userGroup: user?.userGroup
      });

      let bookings = [];

      let hostBookings = [];
      let guestTrips = [];

      if (user?.primaryRole === 'host') {
        console.log('Loading bookings and trips for host...');

        try {
          // Hosts need both their bookings (as hosts) and trips (as guests)
          const [bookingsResponse, tripsResponse] = await Promise.all([
            apiClient.get('/bookings'),
            apiClient.get('/my-trips')
          ]);

          console.log('Raw bookings response:', bookingsResponse);
          console.log('Raw trips response:', tripsResponse);

          // The APIs return the data directly, not wrapped in a data property
          hostBookings = Array.isArray(bookingsResponse.data) ? bookingsResponse.data : [];
          guestTrips = Array.isArray(tripsResponse.data) ? tripsResponse.data : [];

          console.log('Bookings API response data:', bookingsResponse.data);
          console.log('My-trips API response data:', tripsResponse.data);
          console.log('Extracted host bookings:', hostBookings.length, hostBookings);
          console.log('Extracted guest trips:', guestTrips.length, guestTrips);
        } catch (apiError) {
          console.error('Error calling APIs:', apiError);
          setError('Failed to load bookings and trips');
          return;
        }
      } else {
        console.log('Loading trips for guest...');
        try {
          const response = await apiClient.get('/my-trips');
          console.log('Raw trips response:', response);

          guestTrips = Array.isArray(response.data) ? response.data : [];
          console.log('My-trips API response data:', response.data);
          console.log('Extracted guest trips:', guestTrips.length, guestTrips);
        } catch (apiError) {
          console.error('Error calling my-trips API:', apiError);
          setError('Failed to load trips');
          return;
        }
      }

      console.log('Total host bookings:', hostBookings.length);
      console.log('Total guest trips:', guestTrips.length);

      // Convert bookings/trips to conversation format and segregate them
      const hostConversations = hostBookings.map((booking: any) => ({
        bookingId: booking.bookingId,
        listingTitle: booking.listingTitle || booking.title || `Booking ${booking.bookingId}`,
        checkInDate: booking.checkInDate || booking.startDate,
        checkOutDate: booking.checkOutDate || booking.endDate,
        status: booking.status,
        userRole: 'host' as const,
        otherParticipant: {
          email: booking.guestEmail || booking.primaryGuestEmail,
          name: booking.guestName || booking.primaryGuestName || booking.guestEmail || booking.primaryGuestEmail,
          type: 'guest' as const
        },
        lastMessage: null,
        unreadCount: 0,
        lastMessageAt: booking.createdAt,
        createdAt: booking.createdAt
      }));

      const guestConversations = guestTrips.map((trip: any) => ({
        bookingId: trip.bookingId,
        listingTitle: trip.listingTitle || trip.title || `Booking ${trip.bookingId}`,
        checkInDate: trip.checkInDate || trip.startDate,
        checkOutDate: trip.checkOutDate || trip.endDate,
        status: trip.status,
        userRole: 'guest' as const,
        otherParticipant: {
          email: trip.hostEmail || trip.hostId,
          name: trip.hostName || trip.hostEmail || trip.hostId,
          type: 'host' as const
        },
        lastMessage: null,
        unreadCount: 0,
        lastMessageAt: trip.createdAt,
        createdAt: trip.createdAt
      }));

      // Combine conversations with segregation info
      const allConversations: Conversation[] = [
        ...hostConversations.map((conv: any) => ({ ...conv, section: 'host' as const })),
        ...guestConversations.map((conv: any) => ({ ...conv, section: 'guest' as const }))
      ];

      console.log('Host conversations:', hostConversations.length, hostConversations);
      console.log('Guest conversations:', guestConversations.length, guestConversations);
      console.log('Total conversations:', allConversations.length, allConversations);

      setConversations(allConversations);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [user?.email, user?.primaryRole, user?.userGroup]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Paper elevation={1} sx={{ height: '600px', p: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Messages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Conversations List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {conversations.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            color="text.secondary"
            sx={{ p: 3 }}
          >
            <MessageIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1" textAlign="center">
              No conversations yet
            </Typography>
            <Typography variant="body2" textAlign="center">
              {user?.primaryRole === 'host'
                ? 'Messages will appear here when you have bookings or trips'
                : 'Messages will appear here when you have trips booked'
              }
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {/* Host Bookings Section */}
            {user?.primaryRole === 'host' && conversations.filter(c => c.section === 'host').length > 0 && (
              <>
                <ListItem sx={{ py: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight="bold">
                    My Properties ({conversations.filter(c => c.section === 'host').length})
                  </Typography>
                </ListItem>
                {conversations
                  .filter(c => c.section === 'host')
                  .map((conversation, index) => (
                    <React.Fragment key={`host-${conversation.bookingId}`}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedBookingId === conversation.bookingId}
                          onClick={() => onConversationSelect(conversation)}
                          sx={{ py: 2, pl: 3 }}
                        >
                          <ListItemAvatar>
                            <Badge
                              badgeContent={conversation.unreadCount}
                              color="primary"
                              invisible={conversation.unreadCount === 0}
                            >
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <PersonIcon />
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                                  {conversation.otherParticipant.name}
                                </Typography>
                                <Chip label="Host" size="small" color="primary" variant="outlined" />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(conversation.lastMessageAt)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                                  {conversation.listingTitle}
                                </Typography>

                                {conversation.lastMessage ? (
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {conversation.lastMessage.senderEmail === user?.email ? 'You: ' : ''}
                                    {conversation.lastMessage.content}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    No messages yet
                                  </Typography>
                                )}

                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <Chip
                                    label={conversation.status}
                                    size="small"
                                    color={getStatusColor(conversation.status) as any}
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(conversation.checkInDate).toLocaleDateString()} - {new Date(conversation.checkOutDate).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
              </>
            )}

            {/* Guest Trips Section */}
            {conversations.filter(c => c.section === 'guest').length > 0 && (
              <>
                {user?.primaryRole === 'host' && <Divider sx={{ my: 1 }} />}
                <ListItem sx={{ py: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="secondary" fontWeight="bold">
                    {user?.primaryRole === 'host' ? 'My Trips' : 'My Conversations'} ({conversations.filter(c => c.section === 'guest').length})
                  </Typography>
                </ListItem>
                {conversations
                  .filter(c => c.section === 'guest')
                  .map((conversation, index) => (
                    <React.Fragment key={`guest-${conversation.bookingId}`}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedBookingId === conversation.bookingId}
                          onClick={() => onConversationSelect(conversation)}
                          sx={{ py: 2, pl: 3 }}
                        >
                          <ListItemAvatar>
                            <Badge
                              badgeContent={conversation.unreadCount}
                              color="primary"
                              invisible={conversation.unreadCount === 0}
                            >
                              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                <HomeIcon />
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                                  {conversation.otherParticipant.name}
                                </Typography>
                                <Chip label="Guest" size="small" color="secondary" variant="outlined" />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(conversation.lastMessageAt)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                                  {conversation.listingTitle}
                                </Typography>

                                {conversation.lastMessage ? (
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {conversation.lastMessage.senderEmail === user?.email ? 'You: ' : ''}
                                    {conversation.lastMessage.content}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    No messages yet
                                  </Typography>
                                )}

                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <Chip
                                    label={conversation.status}
                                    size="small"
                                    color={getStatusColor(conversation.status) as any}
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(conversation.checkInDate).toLocaleDateString()} - {new Date(conversation.checkOutDate).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
              </>
            )}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default ConversationsList;